import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Plus, ShieldCheck, Users, Radio, Handshake, Wrench, RefreshCw, Phone, RotateCcw, Zap, Shield, Download, Mail,
  DollarSign, TrendingUp, Star, Search, MousePointerClick, Store, Settings as SettingsIco, ArrowLeft, Target,
  HeartPulse, AlertTriangle, MailCheck, Trash2, Archive, BarChart3,
} from "lucide-react";
import { sr } from "../../../lib/sr";
import { auth } from "../../../lib/api";
import { Head, Honest } from "./shared";

const AUTH_OPTS = [
  { key: "RECOMMEND_ONLY", label: "Recommend only", desc: "Every change needs your approval." },
  { key: "GUARDED_AUTOPILOT", label: "Guarded autopilot", desc: "AI adjusts pacing & creative within limits." },
  { key: "MANAGED_AUTOPILOT", label: "Managed autopilot", desc: "Team & AI act within your approved scope." },
];
export function Autopilot() {
  const [tenant, setTenant] = useState(null);
  const [actions, setActions] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => { sr.home().then((r) => setTenant(r.tenant)).catch(() => {}); sr.listActions().then((r) => setActions(r.actions || [])).catch(() => {}); };
  useEffect(() => { load(); }, []);
  const setAuth = async (a) => { try { const r = await sr.setAuthority({ ai_authority: a }); setTenant(r.tenant); toast.success("Authority updated"); } catch { toast.error("Failed"); } };
  const optimize = async () => { setBusy(true); try { const r = await sr.runOptimize(); toast.success(r.auto_applied ? `${r.auto_applied} changes auto-applied` : `${r.awaiting_approval} recommendations for your approval`); load(); } catch { toast.error("Failed"); } finally { setBusy(false); } };
  const rollback = async (id) => { try { await sr.rollbackAction(id); toast.success("Rolled back"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const authority = tenant?.ai_authority || "RECOMMEND_ONLY";
  return (
    <div>
      <Head title="Agent Autopilot" sub="Let AI optimize pacing, frequency and creative within limits you set — every change logged and reversible." />
      <div className="sr-card">
        <div className="sr-eyebrow" style={{ marginBottom: 12 }}><Shield size={13} /> AI authority</div>
        <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }}>
          {AUTH_OPTS.map((a) => (
            <button key={a.key} className="sr-card sr-card-hover" onClick={() => setAuth(a.key)} data-testid={`authority-${a.key}`}
              style={{ cursor: "pointer", textAlign: "left", borderColor: authority === a.key ? "var(--blue)" : "var(--border)", borderWidth: authority === a.key ? 2 : 1 }}>
              <strong style={{ color: "var(--navy)" }}>{a.label}</strong>
              <p className="sr-muted" style={{ fontSize: 13, marginTop: 6 }}>{a.desc}</p>
            </button>
          ))}
        </div>
        <button className="sr-btn sr-btn-primary" style={{ marginTop: 16 }} onClick={optimize} disabled={busy} data-testid="run-optimize-btn">
          {busy ? <><Loader2 size={15} className="sr-spin" /> Optimizing…</> : <><Zap size={15} /> Run optimization</>}
        </button>
        {authority === "RECOMMEND_ONLY" && <p className="sr-muted" style={{ fontSize: 12, marginTop: 10 }}>In "Recommend only", optimization creates suggestions for your approval on Home — nothing changes automatically.</p>}
      </div>

      <h2 style={{ fontSize: 18, marginTop: 24 }}>Change history</h2>
      {actions.length === 0 ? (
        <Honest icon={Zap} title="No optimizations yet" body="Run optimization to see AI-applied and proposed changes here — each one reversible with a single tap." />
      ) : (
        <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
          {actions.map((a) => (
            <div key={a.id} className="sr-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }} data-testid={`action-${a.id}`}>
              <div>
                <span className="sr-badge">{a.agent_type}</span>
                {a.auto ? <span className="sr-badge sr-badge-amber" style={{ marginLeft: 6 }}>Auto</span> : <span className="sr-badge sr-badge-gray" style={{ marginLeft: 6 }}>Approved</span>}
                <span className={`sr-badge ${a.status === "ROLLED_BACK" ? "sr-badge-gray" : "sr-badge-green"}`} style={{ marginLeft: 6 }}>{a.status}</span>
                <p style={{ fontSize: 14, marginTop: 8 }}>{a.rationale}</p>
                <p className="sr-muted" style={{ fontSize: 12 }}>{a.change?.field}: {String(a.change?.before)} → {String(a.change?.to)}</p>
              </div>
              {a.status !== "ROLLED_BACK" && (
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => rollback(a.id)} data-testid={`rollback-${a.id}`}><RotateCcw size={14} /> Roll back</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------- Customers ----------------
const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

function RevenueDetail({ data, editable, onGoalSaved }) {
  const cfg = data.config || {};
  const cpc = data.cpc || 1.5;
  const avgValue = Number(cfg.avg_customer_value) || 0;
  const annual = Number(cfg.annual_return_visits) || 0;
  const [wConv, setWConv] = useState(Number(cfg.conversion_rate) || 0);
  const [wSpend, setWSpend] = useState(0);
  const [goalEdit, setGoalEdit] = useState(false);
  const [goalInput, setGoalInput] = useState(cfg.monthly_revenue_goal || "");
  const [goalSaving, setGoalSaving] = useState(false);
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    if (cfg.monthly_revenue_goal) sr.customersGrowthPlan(data.tenant_id).then(setPlan).catch(() => setPlan(null));
    else setPlan(null);
  }, [cfg.monthly_revenue_goal, data.tenant_id]);
  const saveGoal = async () => {
    const v = parseFloat(goalInput);
    if (!(v > 0)) { toast.error("Enter a target above $0"); return; }
    setGoalSaving(true);
    try { await sr.profileSave({ monthly_revenue_goal: v }); toast.success("Goal set"); setGoalEdit(false); onGoalSaved && onGoalSaved(); }
    catch { toast.error("Couldn't save goal"); } finally { setGoalSaving(false); }
  };
  const extraClicks = cpc ? wSpend / cpc : 0;
  const totalDrivers = (data.visits.driver_count || 0) + (data.clicks.driver_count || 0) + extraClicks;
  const wCustomers = totalDrivers * wConv / 100;
  const wRevenue = wCustomers * avgValue;
  const wLtv = wCustomers * avgValue * annual * 5;
  const curRevenue = (data.visits.revenue_converted || 0) + (data.clicks.revenue_converted || 0);
  const uplift = wRevenue - curRevenue;
  const EV = {
    verified: { label: "Verified", color: "#067647", bg: "#F1FBF4", border: "#B6E3C6" },
    modeled: { label: "Modeled", color: "#B8791B", bg: "#FFF8E6", border: "#F2D98A" },
    projected: { label: "Projected", color: "#0B4DA2", bg: "#EEF4FF", border: "#C9DDFB" },
  };
  const EvidenceBadge = ({ tier, small }) => {
    const e = EV[tier] || EV.modeled;
    return <span className="sr-badge" data-testid={`evidence-${tier}`} style={{ color: e.color, background: e.bg, border: `1px solid ${e.border}`, fontSize: small ? 10 : 11 }}>{e.label}</span>;
  };
  const Metric = ({ icon: Icon, driver, count, label }) => (
    <div className="sr-card" data-testid={`rev-${driver}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <Icon size={18} color="var(--blue)" />
        <strong style={{ color: "var(--navy)" }}>{label}</strong>
        <EvidenceBadge tier="modeled" small />
        <span className="sr-badge sr-badge-gray" style={{ marginLeft: "auto" }} data-testid={`rev-${driver}-count`}>{Number(count.driver_count).toLocaleString()} / 30 days</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <div className="sr-muted" style={{ fontSize: 12 }}>Modeled revenue potential (at {cfg.conversion_rate}% convert)</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#067647" }} data-testid={`rev-${driver}-converted`}>{money(count.revenue_converted)}</div>
          <div className="sr-muted" style={{ fontSize: 12 }}>≈ {Number(count.customers_estimated).toLocaleString()} modeled {driver === "visits" ? "visit-driven" : "click-driven"} customers</div>
        </div>
        <div>
          <div className="sr-muted" style={{ fontSize: 12 }}>If every one converted</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "var(--navy)" }} data-testid={`rev-${driver}-raw`}>{money(count.revenue_raw)}</div>
          <div className="sr-muted" style={{ fontSize: 12 }}>ceiling — 1 {driver === "visits" ? "visit" : "click"} = 1 customer</div>
        </div>
      </div>
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 6 }}>
        <Star size={13} color="var(--gold)" />
        <span style={{ fontSize: 13, color: "var(--navy)" }}>5-year lifetime value: <strong data-testid={`rev-${driver}-ltv`}>{money(count.ltv_5yr)}</strong></span>
      </div>
    </div>
  );
  return (
    <div data-testid="revenue-detail">
      {data.is_estimated && (
        <div className="sr-badge sr-badge-amber" style={{ marginBottom: 12 }} data-testid="rev-estimated-badge">Sample estimate — real numbers appear as visits &amp; clicks come in</div>
      )}
      <div className="sr-card" style={{ marginBottom: 12, background: "#F7FAFF", border: "1px solid #DCE6F5" }} data-testid="rev-evidence-panel">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 8 }}>
          {data.verified && (
            <div className="sr-card" style={{ flex: "1 1 220px", background: "#fff" }} data-testid="rev-verified">
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <EvidenceBadge tier="verified" /> <span className="sr-muted" style={{ fontSize: 12 }}>Confirmed actions · 30 days</span>
              </div>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#067647" }} data-testid="rev-verified-count">{Number(data.verified.count).toLocaleString()}</div>
              <div className="sr-muted" style={{ fontSize: 12 }}>
                redeemed offers &amp; check-ins{data.verified.count ? <> · <EvidenceBadge tier="modeled" small /> ≈ {money(data.verified.modeled_value)} modeled value</> : " — connect your POS to value these"}
              </div>
            </div>
          )}
          <div style={{ flex: "2 1 320px", fontSize: 12, color: "var(--muted)", alignSelf: "center" }} data-testid="rev-indicators">
            <div><b>Period:</b> {data.meta?.period_label || "Last 30 days"} · <b>Currency:</b> {data.meta?.currency || "USD"}{data.meta?.data_freshness ? <> · <b>Latest activity:</b> {new Date(data.meta.data_freshness).toLocaleDateString()}</> : ""}</div>
            <div style={{ marginTop: 4 }}>{data.combined_note}</div>
          </div>
        </div>
        {data.evidence_legend && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, fontSize: 11, color: "var(--muted)", borderTop: "1px solid var(--border)", paddingTop: 8 }} data-testid="rev-legend">
            {Object.entries(data.evidence_legend).map(([k, v]) => (
              <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 4, flex: "1 1 240px" }}><EvidenceBadge tier={k} small /> {v}</span>
            ))}
          </div>
        )}
      </div>
      {(data.goal || goalEdit || editable) && (
        <div className="sr-card" style={{ marginBottom: 14, border: data.goal?.met ? "1px solid #067647" : "1px solid var(--border)" }} data-testid="goal-tracker">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
            <Target size={17} color={data.goal?.met ? "#067647" : "var(--blue)"} />
            <strong style={{ color: "var(--navy)" }}>Monthly revenue-potential goal</strong>
            {data.goal?.met && <span className="sr-badge" style={{ background: "#DCFCE7", color: "#067647" }} data-testid="goal-met-badge">Goal met 🎉</span>}
            {editable && !goalEdit && <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginLeft: "auto" }} onClick={() => { setGoalInput(cfg.monthly_revenue_goal || ""); setGoalEdit(true); }} data-testid="goal-edit-btn">{data.goal ? "Edit goal" : "Set a goal"}</button>}
          </div>
          {goalEdit ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span className="sr-muted" style={{ fontSize: 13 }}>Target $ / month</span>
              <input className="sr-input" type="number" min="0" value={goalInput} onChange={(e) => setGoalInput(e.target.value)} placeholder="10000" data-testid="goal-input" style={{ maxWidth: 160 }} autoFocus />
              <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={saveGoal} disabled={goalSaving} data-testid="goal-save">{goalSaving ? <Loader2 size={14} className="sr-spin" /> : "Save goal"}</button>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setGoalEdit(false)} data-testid="goal-cancel">Cancel</button>
            </div>
          ) : data.goal ? (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
                <span style={{ color: "var(--navy)", fontWeight: 700 }} data-testid="goal-current">{money(data.goal.current)} <span className="sr-muted" style={{ fontWeight: 400 }}>of {money(data.goal.target)} / mo</span></span>
                <span style={{ fontWeight: 800, color: data.goal.met ? "#067647" : "var(--blue)" }} data-testid="goal-progress-pct">{data.goal.progress_pct}%</span>
              </div>
              <div style={{ height: 12, borderRadius: 999, background: "var(--blue-light)", overflow: "hidden" }} data-testid="goal-progress-bar">
                <div style={{ height: "100%", width: `${data.goal.progress_pct}%`, background: data.goal.met ? "#067647" : "var(--blue)", borderRadius: 999, transition: "width .4s" }} />
              </div>
              <div className="sr-muted" style={{ fontSize: 12, marginTop: 6 }} data-testid="goal-remaining">{data.goal.met ? "You're on track to beat your target this month." : `${money(data.goal.remaining)} of potential to go — use the What-if planner below to close the gap.`}</div>
            </>
          ) : (
            <p className="sr-muted" style={{ fontSize: 13, margin: 0 }}>Set a monthly revenue-potential target to track your progress here.</p>
          )}
        </div>
      )}
      {plan && plan.ok && (
        <div className="sr-card" style={{ marginBottom: 14, borderLeft: `4px solid ${plan.on_track ? "#067647" : "var(--blue)"}` }} data-testid="growth-plan">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <TrendingUp size={17} color={plan.on_track ? "#067647" : "var(--blue)"} />
            <strong style={{ color: "var(--navy)" }}>Your growth plan to hit {money(plan.goal)}/mo</strong>
            {plan.on_track && <span className="sr-badge" style={{ background: "#DCFCE7", color: "#067647" }}>On track</span>}
          </div>
          {!plan.on_track && (
            <div className="sr-muted" style={{ fontSize: 13, marginBottom: 8 }} data-testid="growth-gap">
              {money(plan.gap)} of monthly potential to go — here's how to close it:
            </div>
          )}
          <div style={{ display: "grid", gap: 8 }}>
            {plan.recommendations.map((r, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 10px", background: "var(--blue-light)", borderRadius: 8 }} data-testid={`plan-rec-${r.type}`}>
                <div style={{ width: 22, height: 22, borderRadius: 999, background: "#fff", color: "var(--blue)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, flexShrink: 0 }}>{i + 1}</div>
                <div>
                  <strong style={{ fontSize: 13, color: "var(--navy)" }}>{r.title}</strong>
                  <div className="sr-muted" style={{ fontSize: 13 }}>{r.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {!cfg.configured && (
        <div className="sr-card" style={{ background: "#FFF6E6", border: "1px solid #E0A94A", marginBottom: 12 }} data-testid="rev-not-configured">
          <span style={{ fontSize: 13, color: "var(--navy)" }}>Using default economics. Set your real average customer value in <Link to="/app/settings">Settings</Link> for accurate figures.</span>
        </div>
      )}
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
        {[["Avg value / customer", money(cfg.avg_customer_value)], ["Convert rate", `${cfg.conversion_rate}%`], ["Return visits / yr", cfg.annual_return_visits], ["Value per customer (5-yr)", money(data.ltv_per_customer)]].map(([l, v]) => (
          <div key={l} className="sr-card" style={{ flex: "1 1 160px", minWidth: 140 }}>
            <div className="sr-muted" style={{ fontSize: 12 }}>{l}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "var(--navy)" }}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 14 }}>
        <Metric icon={Store} driver="visits" count={data.visits} label="From store visits" />
        <Metric icon={MousePointerClick} driver="clicks" count={data.clicks} label="From ad clicks" />
      </div>

      <div className="sr-card" style={{ marginTop: 14, background: "var(--blue-light)", border: "1px solid var(--blue)" }} data-testid="whatif-panel">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <TrendingUp size={17} color="var(--blue)" />
          <strong style={{ color: "var(--navy)" }}>What if… you improved conversion or spent more?</strong>
        </div>
        <p className="sr-muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 14 }}>Drag to see the revenue you could unlock. Nothing is saved — this is a live projection.</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
          <div>
            <label className="sr-label" style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Conversion rate</span><strong style={{ color: "var(--blue)" }} data-testid="whatif-conversion-value">{wConv.toFixed(1)}%</strong>
            </label>
            <input type="range" min="0" max="50" step="0.5" value={wConv} onChange={(e) => setWConv(parseFloat(e.target.value))}
              data-testid="whatif-conversion" style={{ width: "100%", accentColor: "var(--blue)" }} />
            <div className="sr-muted" style={{ fontSize: 11 }}>Currently {cfg.conversion_rate}%</div>
          </div>
          <div>
            <label className="sr-label" style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Extra ad spend / mo</span><strong style={{ color: "var(--blue)" }} data-testid="whatif-spend-value">+{money(wSpend)}</strong>
            </label>
            <input type="range" min="0" max="5000" step="100" value={wSpend} onChange={(e) => setWSpend(parseFloat(e.target.value))}
              data-testid="whatif-spend" style={{ width: "100%", accentColor: "var(--blue)" }} />
            <div className="sr-muted" style={{ fontSize: 11 }}>≈ {Math.round(extraClicks).toLocaleString()} more clicks at {money(cpc)}/click</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 16 }}>
          <div className="sr-card" style={{ flex: "1 1 180px", background: "#fff" }}>
            <div className="sr-muted" style={{ fontSize: 12 }}>Projected revenue / mo</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: "#067647" }} data-testid="whatif-projected-revenue">{money(wRevenue)}</div>
            {uplift > 0.5 && <div style={{ fontSize: 13, fontWeight: 700, color: "#067647" }} data-testid="whatif-uplift">▲ {money(uplift)} more than today</div>}
          </div>
          <div className="sr-card" style={{ flex: "1 1 180px", background: "#fff" }}>
            <div className="sr-muted" style={{ fontSize: 12 }}>Projected 5-year value</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: "var(--navy)" }} data-testid="whatif-projected-ltv">{money(wLtv)}</div>
            <div className="sr-muted" style={{ fontSize: 12 }}>≈ {Math.round(wCustomers).toLocaleString()} customers / mo</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Customers() {
  const role = auth.getUser()?.role;
  const isStaff = ["admin", "super_admin"].includes(role);
  const isSuperAdmin = role === "super_admin";
  const [own, setOwn] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [q, setQ] = useState("");
  const [mgr, setMgr] = useState("");
  const [category, setCategory] = useState("");
  const [retention, setRetention] = useState("all");
  const [winbackBusy, setWinbackBusy] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmPermanent, setConfirmPermanent] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [detail, setDetail] = useState(null); // {name, data|null}

  useEffect(() => {
    if (isStaff) sr.customersPortfolio().then(setPortfolio).catch(() => setPortfolio({ clients: [], managers: [] }));
    else sr.customersDashboard().then(setOwn).catch(() => setOwn(null));
  }, [isStaff]);

  const openDetail = async (row) => {
    setDetail({ name: row.tenant_name, data: null });
    try { const d = await sr.customersDashboard(row.tenant_id); setDetail({ name: row.tenant_name, data: d }); }
    catch { toast.error("Couldn't load client dashboard"); setDetail(null); }
  };

  const sendWinback = async (row) => {
    setWinbackBusy(row.tenant_id);
    try {
      const r = await sr.customerWinback(row.tenant_id);
      toast.success(r.note || "Win-back sequence started");
      setPortfolio((p) => ({ ...p, clients: (p.clients || []).map((c) => c.tenant_id === row.tenant_id ? { ...c, last_winback_at: r.last_winback_at, winback: r.sequence } : c) }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start win-back"); }
    finally { setWinbackBusy(null); }
  };

  const stopWinback = async (row) => {
    setWinbackBusy(row.tenant_id);
    try {
      await sr.customerWinbackStop(row.tenant_id);
      toast.success("Win-back sequence stopped");
      setPortfolio((p) => ({ ...p, clients: (p.clients || []).map((c) => c.tenant_id === row.tenant_id ? { ...c, winback: { ...(c.winback || {}), status: "stopped", stop_reason: "manual" } } : c) }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't stop sequence"); }
    finally { setWinbackBusy(null); }
  };

  const winbackNextLabel = (iso) => {
    if (!iso) return null;
    const days = Math.round((new Date(iso) - new Date()) / 86400000);
    if (days <= 0) return "next touch due";
    return `next in ${days}d`;
  };

  const reloadPortfolio = () => sr.customersPortfolio().then(setPortfolio).catch(() => {});
  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const doBulkArchive = async () => {
    setBulkBusy(true);
    try {
      const r = await sr.customersBulkDelete({ tenant_ids: [...selected], permanent: false });
      toast.success(`Archived ${r.archived} client${r.archived === 1 ? "" : "s"}`);
      setSelected(new Set()); reloadPortfolio();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't archive clients"); }
    finally { setBulkBusy(false); }
  };
  const doBulkPermanent = async () => {
    if (confirmText.trim().toUpperCase() !== "DELETE") { toast.error("Type DELETE to confirm"); return; }
    setBulkBusy(true);
    try {
      const r = await sr.customersBulkDelete({ tenant_ids: [...selected], permanent: true, confirm: "DELETE" });
      toast.success(`Permanently deleted ${r.deleted} client${r.deleted === 1 ? "" : "s"}`);
      setSelected(new Set()); setConfirmPermanent(false); setConfirmText(""); reloadPortfolio();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete clients"); }
    finally { setBulkBusy(false); }
  };

  if (!isStaff) {
    return (
      <div>
        <Head title="Customers" sub="Your revenue potential and 5-year customer lifetime value, from visits and ad clicks." />
        {!own ? <div className="sr-empty"><Loader2 className="sr-spin" /></div> : <RevenueDetail data={own} editable onGoalSaved={() => sr.customersDashboard().then(setOwn).catch(() => {})} />}
      </div>
    );
  }

  const RET = {
    active: { tone: "#067647", bg: "#F1FBF4", border: "#B6E3C6", label: "Active" },
    at_risk: { tone: "#B8791B", bg: "#FFF8E6", border: "#F2D98A", label: "At risk" },
    lapsed: { tone: "#B42318", bg: "#FEF3F2", border: "#F5C4C0", label: "Lapsed" },
  };
  const summary = portfolio?.retention_summary || { active: 0, at_risk: 0, lapsed: 0 };
  const rows = (portfolio?.clients || []).filter((r) =>
    (!q || (r.tenant_name || "").toLowerCase().includes(q.toLowerCase())) &&
    (!mgr || r.assigned_manager_id === mgr) &&
    (!category || r.category === category) &&
    (retention === "all" || r.retention_status === retention));
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.tenant_id));
  const toggleSelectAll = () => setSelected((s) => {
    const n = new Set(s);
    if (rows.every((r) => n.has(r.tenant_id))) rows.forEach((r) => n.delete(r.tenant_id));
    else rows.forEach((r) => n.add(r.tenant_id));
    return n;
  });

  return (
    <div>
      <Head title="Customers" sub="Every client's revenue potential and 5-year lifetime value — search and filter, then open a client for detail." />
      {!portfolio ? <div className="sr-empty"><Loader2 className="sr-spin" /></div> : (
        <>
          <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: 16 }} data-testid="retention-summary">
            {[["active", "Active clients", summary.active], ["at_risk", "At risk (30–60d quiet)", summary.at_risk], ["lapsed", "Lapsed (60d+ quiet)", summary.lapsed]].map(([key, label, val]) => {
              const cfg = RET[key];
              const on = retention === key;
              return (
                <button key={key} onClick={() => setRetention(on ? "all" : key)} data-testid={`retention-card-${key}`}
                  className="sr-stat" style={{ textAlign: "left", cursor: "pointer", border: "1px solid " + (on ? cfg.tone : cfg.border), background: cfg.bg }}>
                  <div className="sr-stat-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {key === "active" ? <HeartPulse size={13} style={{ color: cfg.tone }} /> : <AlertTriangle size={13} style={{ color: cfg.tone }} />} {label}
                  </div>
                  <div className="sr-stat-val" style={{ color: cfg.tone }}>{val}</div>
                </button>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14, alignItems: "center" }}>
            <div style={{ position: "relative", flex: "1 1 240px" }}>
              <Search size={15} style={{ position: "absolute", left: 10, top: 11, color: "var(--muted)" }} />
              <input className="sr-input" style={{ paddingLeft: 32 }} placeholder="Search clients by name" value={q} onChange={(e) => setQ(e.target.value)} data-testid="cust-search" />
            </div>
            <select className="sr-select" value={mgr} onChange={(e) => setMgr(e.target.value)} data-testid="cust-manager-filter" style={{ maxWidth: 240 }}>
              <option value="">All managers</option>
              {(portfolio.managers || []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <select className="sr-select" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="cust-category-filter" style={{ maxWidth: 220 }}>
              <option value="">All categories</option>
              {(portfolio.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {retention !== "all" && (
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setRetention("all")} data-testid="retention-clear">
                Clear {RET[retention]?.label} filter
              </button>
            )}
          </div>

          {selected.size > 0 && (
            <div data-testid="bulk-action-bar" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", padding: "10px 14px", marginBottom: 10, background: "#FFF8E6", border: "1px solid #F2D98A", borderRadius: 10 }}>
              <strong style={{ color: "var(--navy)" }}>{selected.size} selected</strong>
              <button className="sr-btn sr-btn-sm" disabled={bulkBusy} data-testid="bulk-archive" onClick={doBulkArchive}>
                {bulkBusy ? <Loader2 size={13} className="sr-spin" /> : <Archive size={13} />} Archive
              </button>
              {isSuperAdmin && (
                <button className="sr-btn sr-btn-sm" disabled={bulkBusy} data-testid="bulk-delete-permanent"
                  style={{ color: "#B42318", borderColor: "#F5C4C0" }} onClick={() => setConfirmPermanent(true)}>
                  <Trash2 size={13} /> Delete permanently
                </button>
              )}
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setSelected(new Set())} data-testid="bulk-clear">Clear</button>
            </div>
          )}
          <div className="sr-card" style={{ padding: 0, overflow: "hidden" }} data-testid="cust-portfolio-table">
            <div style={{ display: "grid", gridTemplateColumns: "36px 1.5fr 1fr 0.8fr 1fr 1fr 1.3fr", gap: 8, padding: "10px 14px", background: "var(--blue-light)", fontSize: 12, fontWeight: 700, color: "var(--navy)", alignItems: "center" }}>
              <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} data-testid="cust-select-all" aria-label="Select all" style={{ cursor: "pointer" }} />
              <span>Client</span><span>Manager</span><span>Avg value</span><span>Revenue potential /mo</span><span>5-yr LTV</span><span>Retention</span>
            </div>
            {rows.length === 0 ? <div className="sr-empty" style={{ padding: 24 }}>No clients match.</div> : rows.map((r) => {
              const revMo = r.visits.revenue_converted + r.clicks.revenue_converted;
              const ltv = r.visits.ltv_5yr + r.clicks.ltv_5yr;
              const ret = RET[r.retention_status] || RET.active;
              const quiet = r.days_since_activity == null ? null : r.days_since_activity;
              const showWinback = r.retention_status === "at_risk" || r.retention_status === "lapsed";
              return (
                <div key={r.tenant_id} onClick={() => openDetail(r)} data-testid={`cust-row-${r.tenant_id}`}
                  style={{ display: "grid", gridTemplateColumns: "36px 1.5fr 1fr 0.8fr 1fr 1fr 1.3fr", gap: 8, padding: "12px 14px", alignItems: "center", width: "100%", textAlign: "left", cursor: "pointer", background: selected.has(r.tenant_id) ? "#FFFBEF" : "#fff", borderTop: "1px solid var(--border)" }}>
                  <input type="checkbox" checked={selected.has(r.tenant_id)} onClick={(e) => e.stopPropagation()} onChange={() => toggleSelect(r.tenant_id)} data-testid={`cust-select-${r.tenant_id}`} aria-label={`Select ${r.tenant_name}`} style={{ cursor: "pointer" }} />
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 700, color: "var(--navy)", flexWrap: "wrap" }}>{r.tenant_name}{r.category && <span className="sr-badge sr-badge-gray" data-testid={`cust-category-${r.tenant_id}`}>{r.category}</span>}{r.is_estimated && <span className="sr-badge sr-badge-gray">est.</span>}</span>
                  <span className="sr-muted" style={{ fontSize: 13 }}>{r.assigned_manager_name || "—"}</span>
                  <span>{money(r.avg_customer_value)}</span>
                  <span style={{ color: "#067647", fontWeight: 700 }}>{money(revMo)}</span>
                  <span style={{ fontWeight: 700, color: "var(--navy)" }}>{money(ltv)}</span>
                  <span style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                    <span className="sr-badge" data-testid={`retention-badge-${r.tenant_id}`}
                      style={{ color: ret.tone, background: ret.bg, border: `1px solid ${ret.border}` }}>
                      {r.retention_status === "active" ? <HeartPulse size={11} /> : <AlertTriangle size={11} />} {ret.label}
                      {quiet != null && <span style={{ opacity: 0.75 }}> · {quiet}d</span>}
                    </span>
                    {(() => {
                      const wb = r.winback;
                      if (wb && wb.status === "active") {
                        return (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11 }} data-testid={`winback-active-${r.tenant_id}`}>
                            <span className="sr-badge" style={{ fontSize: 10, color: "#0B4DA2", background: "#EEF4FF", border: "1px solid #C9DDFB" }}>
                              <Mail size={10} /> Win-back {wb.sent}/{wb.total} · {winbackNextLabel(wb.next_touch_at)}
                            </span>
                            <button className="sr-link" data-testid={`winback-stop-${r.tenant_id}`} disabled={winbackBusy === r.tenant_id}
                              onClick={(e) => { e.stopPropagation(); stopWinback(r); }}
                              style={{ fontSize: 10, color: "var(--muted)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>
                              Stop
                            </button>
                          </span>
                        );
                      }
                      if (wb && wb.status === "stopped" && wb.stop_reason === "reactivated") {
                        return <span className="sr-muted" style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }} data-testid={`winback-returned-${r.tenant_id}`}>
                          <HeartPulse size={11} style={{ color: "#067647" }} /> Client returned — sequence stopped
                        </span>;
                      }
                      if (wb && (wb.status === "completed" || wb.status === "stopped")) {
                        return <span className="sr-muted" style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }} data-testid={`winback-done-${r.tenant_id}`}>
                          <MailCheck size={11} style={{ color: "#067647" }} /> Win-back {wb.status === "completed" ? "complete" : "stopped"} ({wb.sent}/{wb.total})
                        </span>;
                      }
                      if (showWinback) {
                        return <button className="sr-btn sr-btn-sm" data-testid={`winback-btn-${r.tenant_id}`} disabled={winbackBusy === r.tenant_id}
                          onClick={(e) => { e.stopPropagation(); sendWinback(r); }}
                          style={{ fontSize: 11, padding: "3px 9px", border: `1px solid ${ret.tone}`, background: "#fff", color: ret.tone }}>
                          {winbackBusy === r.tenant_id ? <Loader2 size={11} className="sr-spin" /> : <Mail size={11} />} Start win-back
                        </button>;
                      }
                      return null;
                    })()}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {detail && (
        <div onClick={(e) => { if (e.target === e.currentTarget) setDetail(null); }}
          style={{ position: "fixed", inset: 0, background: "rgba(11,36,71,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }} data-testid="cust-detail-modal">
          <div className="sr-card" style={{ maxWidth: 780, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ fontSize: 19 }}><TrendingUp size={17} style={{ verticalAlign: "-3px", color: "var(--blue)" }} /> {detail.name}</h3>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setDetail(null)} data-testid="cust-detail-close"><ArrowLeft size={13} /> Back</button>
            </div>
            {!detail.data ? <div className="sr-empty"><Loader2 className="sr-spin" /></div> : <RevenueDetail data={detail.data} />}
          </div>
        </div>
      )}

      {confirmPermanent && (
        <div onClick={(e) => { if (e.target === e.currentTarget) { setConfirmPermanent(false); setConfirmText(""); } }}
          style={{ position: "fixed", inset: 0, background: "rgba(11,36,71,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1001, padding: 16 }} data-testid="bulk-confirm-modal">
          <div className="sr-card" style={{ maxWidth: 460, width: "100%" }}>
            <h3 style={{ fontSize: 18, color: "#B42318", display: "flex", alignItems: "center", gap: 8 }}><Trash2 size={18} /> Permanently delete {selected.size} client{selected.size === 1 ? "" : "s"}?</h3>
            <p className="sr-muted" style={{ fontSize: 14, margin: "10px 0" }}>This removes the selected clients and <b>all</b> of their data. This cannot be undone. Type <b>DELETE</b> to confirm.</p>
            <input className="sr-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" data-testid="bulk-confirm-input" />
            <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => { setConfirmPermanent(false); setConfirmText(""); }} data-testid="bulk-confirm-cancel">Cancel</button>
              <button className="sr-btn sr-btn-sm" disabled={bulkBusy || confirmText.trim().toUpperCase() !== "DELETE"} data-testid="bulk-confirm-delete"
                style={{ background: "#B42318", color: "#fff", opacity: (bulkBusy || confirmText.trim().toUpperCase() !== "DELETE") ? 0.6 : 1 }} onClick={doBulkPermanent}>
                {bulkBusy ? <Loader2 size={13} className="sr-spin" /> : <Trash2 size={13} />} Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Radio ----------------
export function RadioCenter() {
  const [caps, setCaps] = useState(null);
  const [stations, setStations] = useState([]);
  const [dayparts, setDayparts] = useState([]);
  const [flights, setFlights] = useState(null);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({ station_id: "", daypart: "", spots_per_week: 20, weeks: 4, budget_monthly: 1500, plan_id: "" });
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const isStaff = ["admin", "super_admin", "campaign_manager", "compliance_reviewer"].includes(auth.getUser()?.role);
  const [resultsFor, setResultsFor] = useState(null);
  const [rform, setRform] = useState({ spots_aired: "", estimated_reach: "", impressions: "", spend: "" });

  const loadFlights = () => sr.radioFlights().then((r) => setFlights(r.flights)).catch(() => setFlights([]));
  useEffect(() => {
    sr.capabilities().then((r) => setCaps(r.capabilities.filter((c) => ["radio", "radiolife"].includes(c.connector_key)))).catch(() => setCaps([]));
    sr.radioStations().then((r) => { setStations(r.stations); setDayparts(r.dayparts); }).catch(() => {});
    sr.listPlans().then((r) => setPlans(r.growth_plans || [])).catch(() => {});
    loadFlights();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async () => {
    if (!form.station_id) return toast.error("Choose a FunAsia station");
    if (!form.daypart) return toast.error("Choose a daypart");
    setBusy(true);
    try {
      await sr.createRadioFlight({
        station_id: form.station_id, daypart: form.daypart,
        spots_per_week: Number(form.spots_per_week), weeks: Number(form.weeks),
        budget_monthly: Number(form.budget_monthly), plan_id: form.plan_id || null,
      });
      toast.success("Radio flight created");
      setForm({ station_id: "", daypart: "", spots_per_week: 20, weeks: 4, budget_monthly: 1500, plan_id: "" });
      loadFlights();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create flight"); }
    finally { setBusy(false); }
  };

  const sync = async (id) => {
    setSyncing(id);
    try {
      const r = await sr.syncRadioFlight(id);
      toast.success(r.note || "Synced to your digital plan");
      loadFlights();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not sync flight"); }
    finally { setSyncing(null); }
  };

  const remove = async (id) => {
    try { await sr.deleteRadioFlight(id); loadFlights(); } catch { toast.error("Could not remove flight"); }
  };

  const openResults = (f) => {
    setResultsFor(resultsFor === f.id ? null : f.id);
    const m = f.metrics || {};
    setRform({ spots_aired: m.spots_aired ?? "", estimated_reach: m.estimated_reach ?? "", impressions: m.impressions ?? "", spend: m.spend ?? "" });
  };
  const saveResults = async (id) => {
    setBusy(true);
    try {
      const body = {};
      ["spots_aired", "estimated_reach", "impressions", "spend"].forEach((k) => { if (rform[k] !== "" && rform[k] != null) body[k] = Number(rform[k]); });
      await sr.recordRadioResults(id, body);
      toast.success("Radio results recorded");
      setResultsFor(null); loadFlights();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record results"); }
    finally { setBusy(false); }
  };

  const STAT = { DRAFT: "sr-badge-gray", SYNCED: "sr-badge-green", SCHEDULED: "sr-badge-amber", LIVE: "sr-badge-green" };

  return (
    <div>
      <Head title="Radio Sync" sub="Schedule FunAsia radio flights and sync them to your digital growth plan so on-air and online run in lockstep." />
      <div className="sr-card" style={{ background: "var(--blue-light)", border: "1px solid var(--blue)", display: "flex", gap: 12, alignItems: "center" }}>
        <Radio size={22} color="var(--blue)" />
        <p style={{ fontSize: 14 }}>Radio flights are booked with our operations team and only air after script/asset approval and station confirmation — never before.</p>
      </div>

      <div className="sr-card" style={{ marginTop: 16 }} data-testid="radio-flight-form">
        <div className="sr-eyebrow" style={{ marginBottom: 12 }}>Schedule a radio flight</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="sr-field"><label className="sr-label">Station</label>
            <select className="sr-select" value={form.station_id} onChange={set("station_id")} data-testid="radio-station">
              <option value="">Select a FunAsia station…</option>
              {stations.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.market}</option>)}
            </select></div>
          <div className="sr-field"><label className="sr-label">Daypart</label>
            <select className="sr-select" value={form.daypart} onChange={set("daypart")} data-testid="radio-daypart">
              <option value="">Select a daypart…</option>
              {dayparts.map((d) => <option key={d} value={d}>{d}</option>)}
            </select></div>
          <div className="sr-field"><label className="sr-label">Spots per week</label>
            <input className="sr-input" type="number" value={form.spots_per_week} onChange={set("spots_per_week")} data-testid="radio-spots" /></div>
          <div className="sr-field"><label className="sr-label">Weeks</label>
            <input className="sr-input" type="number" value={form.weeks} onChange={set("weeks")} data-testid="radio-weeks" /></div>
          <div className="sr-field"><label className="sr-label">Monthly radio budget ($)</label>
            <input className="sr-input" type="number" value={form.budget_monthly} onChange={set("budget_monthly")} data-testid="radio-budget" /></div>
          <div className="sr-field"><label className="sr-label">Sync to growth plan (optional)</label>
            <select className="sr-select" value={form.plan_id} onChange={set("plan_id")} data-testid="radio-plan">
              <option value="">Latest approved plan</option>
              {plans.map((p) => <option key={p.id} value={p.id}>v{p.version} · {p.goal} · {p.status}</option>)}
            </select></div>
        </div>
        <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 14 }} onClick={create} disabled={busy} data-testid="radio-create">
          {busy ? <Loader2 size={15} className="sr-spin" /> : <><Plus size={15} /> Create flight</>}
        </button>
      </div>

      <div style={{ marginTop: 20 }}>
        <div className="sr-eyebrow" style={{ marginBottom: 12 }}>Your radio flights</div>
        {flights === null && <div className="sr-empty"><Loader2 className="sr-spin" /></div>}
        {flights && flights.length === 0 && (
          <Honest icon={Radio} title="No radio flights yet" body="Schedule a FunAsia flight above, then sync it to your digital plan so radio and digital hit the same audience during the same weeks." />
        )}
        <div style={{ display: "grid", gap: 10 }}>
          {(flights || []).map((f) => (
            <div key={f.id} className="sr-card" data-testid={`radio-flight-${f.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div>
                  <strong style={{ color: "var(--navy)" }}>{f.station_name}</strong>
                  <span className={`sr-badge ${STAT[f.status] || "sr-badge-gray"}`} style={{ marginLeft: 10 }} data-testid={`radio-status-${f.id}`}>{f.status}</span>
                  <div className="sr-muted" style={{ fontSize: 13, marginTop: 6 }}>
                    {f.daypart} · {f.spots_per_week} spots/wk × {f.weeks}wk · ${Number(f.budget_monthly).toLocaleString()}/mo
                  </div>
                  <div className="sr-muted" style={{ fontSize: 12, marginTop: 4 }}>
                    Flight: {f.start_date} → {f.end_date}
                    {f.status === "SYNCED" && f.synced_campaign_ids?.length > 0 &&
                      <> · synced with {f.synced_campaign_ids.length} digital campaign(s)</>}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  {f.status === "DRAFT" && (
                    <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => sync(f.id)} disabled={syncing === f.id} data-testid={`radio-sync-${f.id}`}>
                      {syncing === f.id ? <Loader2 size={14} className="sr-spin" /> : <><RefreshCw size={14} /> Sync to plan</>}
                    </button>
                  )}
                  {isStaff && (
                    <button className="sr-btn sr-btn-sm" onClick={() => openResults(f)} data-testid={`radio-results-btn-${f.id}`}><BarChart3 size={14} /> Results</button>
                  )}
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => remove(f.id)} data-testid={`radio-delete-${f.id}`}>Remove</button>
                </div>
              </div>
              {f.metrics && (f.metrics.spots_aired != null || f.metrics.estimated_reach != null || f.metrics.spend != null) && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }} data-testid={`radio-metrics-${f.id}`}>
                  {f.metrics.spots_aired != null && <span className="sr-badge sr-badge-green">{Number(f.metrics.spots_aired).toLocaleString()} spots aired</span>}
                  {f.metrics.estimated_reach != null && <span className="sr-badge sr-badge-gray">Est. reach {Number(f.metrics.estimated_reach).toLocaleString()}</span>}
                  {f.metrics.impressions != null && <span className="sr-badge sr-badge-gray">{Number(f.metrics.impressions).toLocaleString()} impressions</span>}
                  {f.metrics.spend != null && <span className="sr-badge" style={{ background: "#FBEFD9", color: "#9A7412" }}>${Number(f.metrics.spend).toLocaleString()} spend</span>}
                </div>
              )}
              {isStaff && resultsFor === f.id && (
                <div style={{ marginTop: 12, borderTop: "1px dashed var(--border)", paddingTop: 12 }} data-testid={`radio-results-form-${f.id}`}>
                  <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Record aired results</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 }}>
                    <div className="sr-field"><label className="sr-label">Spots aired</label><input className="sr-input" type="number" value={rform.spots_aired} onChange={(e) => setRform((s) => ({ ...s, spots_aired: e.target.value }))} data-testid={`radio-spots-aired-${f.id}`} /></div>
                    <div className="sr-field"><label className="sr-label">Estimated reach</label><input className="sr-input" type="number" value={rform.estimated_reach} onChange={(e) => setRform((s) => ({ ...s, estimated_reach: e.target.value }))} data-testid={`radio-reach-${f.id}`} /></div>
                    <div className="sr-field"><label className="sr-label">Impressions</label><input className="sr-input" type="number" value={rform.impressions} onChange={(e) => setRform((s) => ({ ...s, impressions: e.target.value }))} data-testid={`radio-impressions-${f.id}`} /></div>
                    <div className="sr-field"><label className="sr-label">Actual spend ($)</label><input className="sr-input" type="number" value={rform.spend} onChange={(e) => setRform((s) => ({ ...s, spend: e.target.value }))} data-testid={`radio-spend-${f.id}`} /></div>
                  </div>
                  <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 10 }} disabled={busy} onClick={() => saveResults(f.id)} data-testid={`radio-results-save-${f.id}`}>Save results</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <div className="sr-eyebrow" style={{ marginBottom: 12 }}>Radio & loyalty connectors</div>
        <div style={{ display: "grid", gap: 10 }}>
          {(caps || []).map((c) => (
            <div key={`${c.connector_key}-${c.channel}`} className="sr-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div><strong style={{ color: "var(--navy)", textTransform: "capitalize" }}>{c.connector_key.replace(/_/g, " ")}</strong>
                <div className="sr-muted" style={{ fontSize: 13 }}>{c.note}</div></div>
              <span className="sr-badge sr-badge-amber">Managed setup · pending activation</span>
            </div>
          ))}
          {caps === null && <div className="sr-empty"><Loader2 className="sr-spin" /></div>}
        </div>
      </div>
    </div>
  );
}

// ---------------- Services ----------------
export function Services() {
  const [home, setHome] = useState(null);
  useEffect(() => { sr.home().then(setHome).catch(() => {}); }, []);
  const mode = home?.tenant?.service_mode || "SELF_SERVICE";
  const authority = home?.tenant?.ai_authority || "RECOMMEND_ONLY";
  return (
    <div>
      <Head title="Services" sub="Your service mode, assigned team, deliverables and upgrade path." />
      <div className="sr-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <div className="sr-card"><div className="sr-eyebrow" style={{ marginBottom: 8 }}>Current service mode</div>
          <strong style={{ color: "var(--navy)", fontSize: 18 }}>{mode.replace(/_/g, " ")}</strong>
          <p className="sr-muted" style={{ fontSize: 13, marginTop: 6 }}>AI authority: {authority.replace(/_/g, " ")}</p></div>
        <div className="sr-card"><div className="sr-eyebrow" style={{ marginBottom: 8 }}>Upgrade to Professional</div>
          <p className="sr-muted" style={{ fontSize: 14 }}>Get a dedicated team for strategy, creative, media and radio. Your campaigns and history are preserved — nothing is recreated.</p>
          <Link to="/pricing" className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 12 }}>See plans</Link></div>
      </div>
      <Honest icon={Handshake} title="No active professional engagement" body="When you move to Professional Service, your assigned team, deliverables, SLAs and quarterly plan appear here." />
    </div>
  );
}

// ---------------- Settings ----------------
export function Settings() {
  const [profile, setProfile] = useState(null);
  const [caps, setCaps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [consentLog, setConsentLog] = useState(null);
  const [smsCfg, setSmsCfg] = useState(null);
  const [emailPrev, setEmailPrev] = useState(null);
  const openEmailPreview = async () => {
    try { const r = await sr.emailPreview(); setEmailPrev(r.previews || []); }
    catch { toast.error("Couldn't load email preview"); }
  };
  useEffect(() => {
    sr.profileGet().then((r) => setProfile(r.profile || {})).catch(() => setProfile({}));
    sr.capabilities().then((r) => setCaps(r.capabilities)).catch(() => {});
    sr.smsConsentLog().then((r) => setConsentLog(r.log || [])).catch(() => setConsentLog([]));
    sr.getSmsSettings().then(setSmsCfg).catch(() => {});
  }, []);
  const toggleSms = async (val) => {
    try { const r = await sr.setSmsSettings(val); setSmsCfg((c) => ({ ...(c || {}), enabled: r.enabled })); toast.success(val ? "Text messaging enabled" : "Text messaging paused — using email"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update"); }
  };
  const save = async () => { setSaving(true); try { const payload = { ...profile }; ["avg_customer_value", "conversion_rate", "annual_return_visits", "monthly_revenue_goal"].forEach((k) => { if (payload[k] === "" || payload[k] === null || payload[k] === undefined) delete payload[k]; else payload[k] = Number(payload[k]); }); await sr.profileSave(payload); toast.success("Saved"); sr.smsConsentLog().then((r) => setConsentLog(r.log || [])).catch(() => {}); } catch { toast.error("Failed"); } finally { setSaving(false); } };
  const downloadConsentCsv = async () => { try { const blob = await sr.smsConsentCsv(); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "sms-consent-log.csv"; a.click(); URL.revokeObjectURL(url); } catch { toast.error("Could not download"); } };
  const [testTo, setTestTo] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const isStaff = ["admin", "super_admin"].includes(auth.getUser()?.role);
  const sendTestSms = async () => { if (!testTo.trim()) { toast.error("Enter a phone number"); return; } setTestBusy(true); try { const r = await sr.smsTest(testTo.trim()); toast.success(`Test SMS sent (${r.sid?.slice(0, 10)}…)`); } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); } setTestBusy(false); };
  if (!profile) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  const set = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });
  const CAP = { AVAILABLE: "sr-badge-green", CONDITIONAL: "sr-badge-amber", MANUAL: "sr-badge", UNAVAILABLE: "sr-badge-gray" };
  return (
    <div>
      <Head title="Settings" sub="Brand, integrations, billing and privacy." />
      <div className="sr-card">
        <div className="sr-eyebrow" style={{ marginBottom: 12 }}>Business profile</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="sr-field"><label className="sr-label">Name</label><input className="sr-input" value={profile.name || ""} onChange={set("name")} data-testid="settings-name" /></div>
          <div className="sr-field"><label className="sr-label">Category</label><input className="sr-input" value={profile.category || ""} onChange={set("category")} /></div>
          <div className="sr-field"><label className="sr-label">Website</label><input className="sr-input" value={profile.website || ""} onChange={set("website")} /></div>
          <div className="sr-field"><label className="sr-label">Phone</label><input className="sr-input" value={profile.phone || ""} onChange={set("phone")} data-testid="settings-phone" /></div>
        </div>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 4, fontSize: 13, color: "var(--navy)", cursor: "pointer" }} data-testid="settings-sms-consent-label">
          <input type="checkbox" checked={!!profile.sms_consent} onChange={(e) => setProfile({ ...profile, sms_consent: e.target.checked })} data-testid="settings-sms-consent" style={{ marginTop: 3 }} />
          <span>I agree to receive account &amp; service text messages (SMS) from SmartReachAI at this number, including reconnect and billing alerts. Message frequency varies. Message &amp; data rates may apply. Reply STOP to opt out, HELP for help. See our <a href="/sms-terms.html" target="_blank" rel="noreferrer">SMS Terms</a> and <a href="/privacy.html" target="_blank" rel="noreferrer">Privacy Policy</a>.{profile.sms_consent_at ? ` (Consent recorded ${new Date(profile.sms_consent_at).toLocaleDateString()})` : ""}</span>
        </label>
        <div className="sr-field"><label className="sr-label">Address</label><input className="sr-input" value={profile.address || ""} onChange={set("address")} /></div>
        <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={save} disabled={saving} data-testid="settings-save">{saving ? <Loader2 size={15} className="sr-spin" /> : "Save"}</button>
      </div>
      <div className="sr-card" style={{ marginTop: 16 }} data-testid="settings-customer-value">
        <div className="sr-eyebrow" style={{ marginBottom: 4 }}>Customer value &amp; lifetime value</div>
        <p className="sr-muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 12 }}>Powers your Customers revenue-potential dashboard. Estimates are fine — how often a customer returns sets their 5-year value.</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div className="sr-field"><label className="sr-label">Avg value per customer ($)</label>
            <input className="sr-input" type="number" value={profile.avg_customer_value ?? ""} onChange={set("avg_customer_value")} placeholder="45" data-testid="settings-customer-value-input" /></div>
          <div className="sr-field"><label className="sr-label">Visit → customer rate (%)</label>
            <input className="sr-input" type="number" value={profile.conversion_rate ?? ""} onChange={set("conversion_rate")} placeholder="8" data-testid="settings-conversion-input" /></div>
          <div className="sr-field"><label className="sr-label">Return visits / year</label>
            <input className="sr-input" type="number" value={profile.annual_return_visits ?? ""} onChange={set("annual_return_visits")} placeholder="4" data-testid="settings-return-visits-input" /></div>
          <div className="sr-field"><label className="sr-label">Monthly revenue goal ($)</label>
            <input className="sr-input" type="number" value={profile.monthly_revenue_goal ?? ""} onChange={set("monthly_revenue_goal")} placeholder="10000" data-testid="settings-goal-input" /></div>
        </div>
        {(() => { const cv = parseFloat(profile.avg_customer_value || 0) || 0; const rr = parseFloat(profile.annual_return_visits || 0) || 0; const ltv = cv * rr * 5; return ltv ? <p style={{ fontSize: 13, color: "var(--navy)", margin: 0 }} data-testid="settings-ltv-hint"><Star size={12} style={{ verticalAlign: "-1px", color: "var(--gold)" }} /> Each customer is worth ~<strong>${ltv.toLocaleString()}</strong> over 5 years.</p> : null; })()}
        <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 10 }} onClick={save} disabled={saving} data-testid="settings-customer-value-save">{saving ? <Loader2 size={15} className="sr-spin" /> : "Save"}</button>
      </div>
      <div className="sr-card" style={{ marginTop: 16 }}>
        <div className="sr-eyebrow" style={{ marginBottom: 12 }}>Integrations</div>
        <div style={{ display: "grid", gap: 8 }}>
          {caps.map((c) => (
            <div key={`${c.connector_key}-${c.channel}`} style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "6px 0" }}>
              <span style={{ textTransform: "capitalize" }}>{c.connector_key.replace(/_/g, " ")} · {c.channel.replace(/_/g, " ")}</span>
              <span className={`sr-badge ${CAP[c.state] || "sr-badge-gray"}`}>{c.state}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="sr-card" style={{ marginTop: 16 }} data-testid="sms-consent-report">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div className="sr-eyebrow">SMS consent log (audit)</div>
          <button className="sr-btn sr-btn-sm" onClick={downloadConsentCsv} data-testid="consent-csv"><Download size={13} /> Export CSV</button>
        </div>
        {smsCfg && (
          <div style={{ padding: 10, borderRadius: 8, marginBottom: 10, background: smsCfg.enabled ? "#EAF7EE" : "#FFF6E6", border: `1px solid ${smsCfg.enabled ? "#067647" : "#E0A94A"}` }} data-testid="sms-status-banner">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, color: "var(--navy)" }} data-testid="sms-status-text">
                {smsCfg.enabled ? "Text messaging is ON." : "Text messaging is paused until your Twilio number is verified — notifications are being sent by email in the meantime."}
              </span>
              {isStaff && (
                <button className="sr-btn sr-btn-sm" onClick={() => toggleSms(!smsCfg.enabled)} data-testid="sms-toggle-btn">
                  {smsCfg.enabled ? "Pause SMS" : "Enable SMS (Twilio verified)"}
                </button>
              )}
            </div>
            {!smsCfg.enabled && (
              <button className="sr-btn sr-btn-sm sr-btn-ghost" style={{ marginTop: 8 }} onClick={openEmailPreview} data-testid="email-preview-btn">
                <Mail size={13} /> Preview the email clients get instead
              </button>
            )}
          </div>
        )}
        {emailPrev && (
          <div onClick={(e) => { if (e.target === e.currentTarget) setEmailPrev(null); }}
            style={{ position: "fixed", inset: 0, background: "rgba(11,36,71,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }} data-testid="email-preview-modal">
            <div className="sr-card" style={{ maxWidth: 640, width: "100%", maxHeight: "88vh", overflow: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <h3 style={{ fontSize: 18 }}>Email notifications (while SMS is paused)</h3>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setEmailPrev(null)} data-testid="email-preview-close">Close</button>
              </div>
              {emailPrev.length === 0 ? <p className="sr-muted">No email templates to preview.</p> : emailPrev.map((p, i) => (
                <div key={i} style={{ marginBottom: 16 }} data-testid={`email-preview-${i}`}>
                  <div className="sr-muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em" }}>{p.label}</div>
                  <div style={{ fontWeight: 700, color: "var(--navy)", margin: "4px 0 8px" }}>Subject: {p.subject}</div>
                  <iframe title={`email-${i}`} srcDoc={p.html} style={{ width: "100%", height: 300, border: "1px solid var(--border)", borderRadius: 8, background: "#fff" }} />
                </div>
              ))}
            </div>
          </div>
        )}
        {isStaff && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }} data-testid="sms-test-row">
            <input className="sr-input" style={{ maxWidth: 220 }} placeholder="+1 555 123 4567" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="sms-test-to" />
            <button className="sr-btn sr-btn-sm" onClick={sendTestSms} disabled={testBusy} data-testid="sms-test-send">{testBusy ? <Loader2 size={13} className="sr-spin" /> : <><Phone size={13} /> Send test SMS</>}</button>
            <span className="sr-muted" style={{ fontSize: 12 }}>Confirm Twilio delivery to a verified number</span>
          </div>
        )}
        {consentLog === null ? <Loader2 size={16} className="sr-spin" />
          : consentLog.length === 0 ? <p className="sr-muted" style={{ fontSize: 13 }}>No SMS consent events yet. Consent is recorded when a phone number opts in or out.</p>
            : (
              <div style={{ maxHeight: 220, overflowY: "auto" }}>
                {consentLog.slice(0, 50).map((r) => (
                  <div key={r.id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderTop: "1px solid var(--border)", fontSize: 13, gap: 8 }} data-testid={`consent-row-${r.id}`}>
                    <span><span className={`sr-badge ${r.action === "opt_in" ? "sr-badge-green" : "sr-badge-gray"}`}>{r.action === "opt_in" ? "Opt-in" : "Opt-out"}</span> {r.phone || "—"}</span>
                    <span className="sr-muted">{r.source ? `${r.source} · ` : ""}{new Date(r.at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
      </div>
      <p className="sr-muted" style={{ fontSize: 12, marginTop: 14 }}><ShieldCheck size={11} /> Aggregate reporting only. We honor consent, retention and deletion, and block sensitive categories.</p>
    </div>
  );
}

// ---------------- Ops console (staff only) ----------------
export function Ops() {
  const user = auth.getUser();
  const isStaff = user && ["admin", "super_admin", "campaign_manager", "compliance_reviewer"].includes(user.role);
  const [caps, setCaps] = useState(null);
  const [tasks, setTasks] = useState([]);
  const loadTasks = () => sr.opsTasks().then((r) => setTasks(r.tasks || [])).catch(() => {});
  useEffect(() => { if (isStaff) { sr.capabilities().then((r) => setCaps(r.capabilities)).catch(() => setCaps([])); loadTasks(); } }, [isStaff]);
  const confirm = async (id) => { try { await sr.confirmTask(id); toast.success("Activation confirmed — campaign is live"); loadTasks(); } catch { toast.error("Failed"); } };
  if (!isStaff) return (
    <div className="sr-root"><div className="sr-content"><Honest icon={Wrench} title="Operations is staff-only" body="This console is restricted to operators." /></div></div>
  );
  const CAP = { AVAILABLE: "sr-badge-green", CONDITIONAL: "sr-badge-amber", MANUAL: "sr-badge", UNAVAILABLE: "sr-badge-gray" };
  const openTasks = tasks.filter((t) => t.status === "open");
  return (
    <div className="sr-root"><div className="sr-content" style={{ maxWidth: 1000 }}>
      <Head title="Operations Console" sub="Connector health, activation tasks and reconciliation. External failures are visible here — never shown to clients as success." />

      <div className="sr-card">
        <div className="sr-eyebrow" style={{ marginBottom: 12 }}>Activation queue ({openTasks.length} open)</div>
        {tasks.length === 0 ? <p className="sr-muted" style={{ fontSize: 14 }}>No activation tasks yet. They appear when a client activates a plan or publishes an offer.</p> : (
          <div style={{ display: "grid", gap: 8 }}>
            {tasks.map((t) => (
              <div key={t.id} className="sr-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 14 }} data-testid={`ops-task-${t.id}`}>
                <div>
                  <strong style={{ color: "var(--navy)" }}>{t.note}</strong>
                  <div className="sr-muted" style={{ fontSize: 12 }}>{t.kind} · {t.connector_key || "—"}{t.channel ? ` · ${t.channel}` : ""}</div>
                </div>
                {t.status === "open"
                  ? <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => confirm(t.id)} data-testid={`ops-confirm-${t.id}`}>Confirm activation</button>
                  : <span className="sr-badge sr-badge-green">Confirmed</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="sr-card" style={{ marginTop: 16 }}>
        <div className="sr-eyebrow" style={{ marginBottom: 12 }}>Connector capability & health</div>
        <table className="sr-table" data-testid="ops-capabilities">
          <thead><tr><th>Connector</th><th>Channel</th><th>Mode</th><th className="center">State</th><th className="center">Polygon</th></tr></thead>
          <tbody>
            {(caps || []).map((c) => (
              <tr key={`${c.connector_key}-${c.channel}`}>
                <td style={{ textTransform: "capitalize" }}>{c.connector_key.replace(/_/g, " ")}</td>
                <td style={{ textTransform: "capitalize" }}>{c.channel.replace(/_/g, " ")}</td>
                <td>{c.activation_mode}</td>
                <td className="center"><span className={`sr-badge ${CAP[c.state] || "sr-badge-gray"}`}>{c.state}</span></td>
                <td className="center">{c.polygon_support ? "Yes" : `${c.min_radius_meters || "—"}m`}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {caps === null && <div className="sr-empty"><Loader2 className="sr-spin" /></div>}
      </div>
    </div></div>
  );
}
